import React, { useMemo, useRef, useState } from 'react';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import StatusBadge from './StatusBadge';
import { showInfo } from '../../utils/toast';
import { parseCsvText } from '../../utils/csvTransfer';
import { EXCEL_ACCEPT, exportToExcel, parseImportFile } from '../../utils/excelTransfer';

const DEFAULT_PAGE_SIZE = 10;

function DataTable({
  columns = [],
  data = [],
  rowKey = '_id',
  searchPlaceholder = 'Search...',
  searchKeys = [],
  onRefresh,
  onImportRows,
  exportFileName = 'export.csv',
  loading = false,
  emptyMessage = 'No records found.',
  pageSize = DEFAULT_PAGE_SIZE,
  showRowNumbers = true,
  hideToolbar = false,
  hideImport = false,
  onRowClick,
}) {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);
  const importInputRef = useRef(null);

  const filtered = useMemo(() => {
    let rows = Array.isArray(data) ? [...data] : [];
    const q = search.trim().toLowerCase();
    if (q && searchKeys.length) {
      rows = rows.filter((row) =>
        searchKeys.some((key) => String(row[key] ?? '').toLowerCase().includes(q))
      );
    }
    if (sortKey) {
      rows.sort((a, b) => {
        const av = String(a[sortKey] ?? '').toLowerCase();
        const bv = String(b[sortKey] ?? '').toLowerCase();
        if (av < bv) return sortDir === 'asc' ? -1 : 1;
        if (av > bv) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return rows;
  }, [data, search, searchKeys, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const renderCell = (col, row) => {
    if (col.type === 'status') {
      return <StatusBadge status={row[col.key]} useStoredLabel={col.useStoredLabel} />;
    }
    if (col.render) return col.render(row);
    return row[col.key] ?? '—';
  };

  const handleImportClick = () => {
    importInputRef.current?.click();
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const rows = await parseImportFile(file, parseCsvText);
      if (!rows.length) {
        showInfo('No data rows found in the imported file.');
        return;
      }
      if (onImportRows) onImportRows(rows);
      else showInfo(`Imported ${rows.length} row(s) from file.`);
    } catch (err) {
      showInfo('Unable to read import file. Please use a valid Excel or CSV file.');
    }
    event.target.value = '';
  };

  return (
    <div className="hr-data-table">
      {!hideToolbar && (
      <div className="hr-table-toolbar">
        <div className="hr-table-toolbar-left">
          <input
            className="hr-search"
            type="search"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="hr-table-toolbar-right">
          {onRefresh && (
            <Button variant="outline-secondary" size="sm" onClick={onRefresh}>
              ↻ Refresh
            </Button>
          )}
          {!hideImport && (
            <>
              <Button variant="outline-secondary" size="sm" onClick={handleImportClick}>
                Import
              </Button>
              <input
                ref={importInputRef}
                type="file"
                accept={EXCEL_ACCEPT}
                className="hr-import-input"
                onChange={handleImportFile}
              />
            </>
          )}
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => exportToExcel(exportFileName.replace(/\.csv$/i, '.xlsx'), columns, filtered)}
            disabled={!filtered.length}
          >
            Export Excel
          </Button>
        </div>
      </div>
      )}

      <div className="hr-table-card">
        {loading ? (
          <p className="hr-empty-state">Loading...</p>
        ) : (
          <Table bordered hover responsive className="hr-table">
            <thead>
              <tr>
                {showRowNumbers && <th>#</th>}
                {columns.map((col) => (
                  <th key={col.key}>
                    {col.sortable ? (
                      <button type="button" className="hr-sort-btn" onClick={() => handleSort(col.key)}>
                        {col.label} {sortKey === col.key ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                      </button>
                    ) : (
                      col.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paged.length ? (
                paged.map((row, index) => (
                  <tr
                    key={row[rowKey] || index}
                    className={onRowClick ? 'hr-table-row-clickable' : undefined}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {showRowNumbers && <td>{(currentPage - 1) * pageSize + index + 1}</td>}
                    {columns.map((col) => (
                      <td key={`${row[rowKey]}-${col.key}`}>{renderCell(col, row)}</td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length + (showRowNumbers ? 1 : 0)} className="hr-empty-state">
                    {emptyMessage}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </div>

      {filtered.length > pageSize && (
        <div className="hr-pagination">
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <span>
            Page {currentPage} of {totalPages} ({filtered.length} records)
          </span>
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={currentPage >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

export default DataTable;
